// The OFFLINE driver: the shared sim + bots running in this tab, behind the same messages the server sends. Used when the
// server is unreachable or the page has ?solo=1. It steps the very same Table the server runs, encodes the very same
// snapshots (public + this seat's private block) and routes the very same events, so the renderer and the HUD cannot
// tell the difference — and neither can the gates.
import { Table } from "../../../shared/table";
import { DT } from "../../../shared/sim";
import * as T from "../../../shared/tuning";
import { PROTOCOL_VERSION, encodePublic, encodePrivate, wireEvent, cleanCos, type JoinOptions, type InputMsg, type WireEvent, type PopMsg, type MarkMsg, type WhistleMsg } from "../../../shared/protocol";
import type { Cosmetics } from "../../../shared/sim";
import type { Transport, TransportHandlers } from "./transport";

const SEAT = 0;
const MAX_CATCHUP = 0.25;   // a tab that slept does not fast-forward more than this

export class OfflineTransport implements Transport {
  readonly kind = "offline" as const;
  readonly table: Table;
  private queue: InputMsg[] = []; private ack = 0; private acc = 0; private last = -1; private snapN = 0;

  constructor(private h: TransportHandlers, join: JoinOptions, seed = (Date.now() % 1_000_000_007) >>> 0 || 1) {
    this.table = new Table(seed, { wallMs: Date.now() });
    this.table.seatHuman(SEAT, { name: String(join.name || "You").slice(0, 20), pid: join.pid || "", cos: cleanCos(join.cos), newcomer: !!join.first });
    // QA only (offline table, never the server): ?as=inspector seats you as the Inspector for the first round
    if (typeof location !== "undefined" && new URLSearchParams(location.search).get("as") === "inspector") {
      const sim = this.table.sim as any; sim.seats[SEAT].newcomer = false; sim.seats[SEAT].roundsPlayed = 1; sim.nextInspectorSeat = SEAT; sim.startRound();
    } else this.table.sim.restartRound();   // your round starts now (an onboarding round on a first visit)
    queueMicrotask(() => h.seat({ v: PROTOCOL_VERSION, seat: SEAT, code: "", roomId: "offline", name: this.table.sim.seats[SEAT].name }));
  }

  pump(nowMs: number) {
    if (this.last < 0) { this.last = nowMs; this.emitSnap(nowMs, true); return; }
    this.acc += Math.min(MAX_CATCHUP, (nowMs - this.last) / 1000); this.last = nowMs;
    const sim = this.table.sim, out: WireEvent[] = [];
    let snap = false;
    while (this.acc >= DT) {
      this.acc -= DT;
      sim.wallMs = Date.now();
      const m = this.queue.shift();
      if (m) { sim.setInput(SEAT, { mx: m.mx, mz: m.mz, sprint: !!m.sp, act: !!m.a }); this.ack = m.s; }
      for (const e of this.table.step()) if (!("to" in e) || e.to === SEAT) out.push(wireEvent(e));
      if (Math.floor(((sim.tick - 1) * T.SNAP_HZ) / T.SIM_HZ) !== Math.floor((sim.tick * T.SNAP_HZ) / T.SIM_HZ)) snap = true;
    }
    if (out.length) this.h.events(out);
    if (snap) this.emitSnap(nowMs, ++this.snapN % (2 * T.SNAP_HZ) === 1);
  }
  private emitSnap(nowMs: number, roster: boolean) {
    const sim = this.table.sim;
    this.h.snap({ p: encodePublic(this.table.pub, roster), m: encodePrivate(sim.privateView(SEAT), this.ack) }, nowMs);
  }

  input(m: InputMsg) { this.queue.push(m); if (this.queue.length > T.INPUT_QUEUE_MAX) this.queue.splice(0, this.queue.length - T.INPUT_QUEUE_MAX); }
  pop(m: PopMsg) { const p = m.p; this.table.sim.pop(SEAT, m.id | 0, p ? { x: p[0], y: p[1], z: p[2] } : null); }
  mark(m: MarkMsg) { this.table.sim.mark(SEAT, m.id | 0, { x: m.p[0], y: m.p[1], z: m.p[2] }); }
  whistle(m: WhistleMsg) { this.table.sim.whistleCall(SEAT, m.c); }
  cos(c: Partial<Cosmetics>) { this.table.sim.seats[SEAT].cos = cleanCos(c); }
  name(n: string) { this.table.sim.seats[SEAT].name = String(n).slice(0, 20) || this.table.sim.seats[SEAT].name; }
  close() { this.queue = []; }
}
